/* aa-hero.js — the hero's endless product row (PLAN.md I1.1, Figma 267:4765)
 *
 * WHY scrollLeft AND NOT A TRANSFORM. A translated track is smoother to write and impossible to
 * grab: the browser has nothing to scroll, so a finger swipe does nothing and a trackpad flick does
 * nothing. This is a real scroll container that happens to drift, so every native gesture — touch
 * drag, trackpad, shift-wheel, keyboard arrows once focused — works without a line of code, and the
 * drift simply steps aside while someone is using it.
 *
 * WHY THE LIST IS RENDERED TWICE. The wrap is `scrollLeft -= half`, which is invisible only
 * because the pixels either side of the seam are identical. The section renders the products, then
 * renders them again; this file never clones anything, so there is nothing to keep in sync.
 *
 * It stops for: a pointer over it, a finger on it, focus inside it, a hidden tab, being scrolled
 * out of view, and `prefers-reduced-motion`, where it never starts.
 */

class AAHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.aa-hero__track');
    if (!this.track) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.held = 0; // how many reasons there are to stay still
    this.frame = null;
    this.last = 0;
    this.remainder = 0;

    /* px per second. Slow enough to read a card in passing, fast enough to look alive. */
    this.speed = 24;

    this.onFrame = this.onFrame.bind(this);

    this.hold = this.hold.bind(this);
    this.release = this.release.bind(this);

    /* Pointer covers mouse, pen and touch. `pointercancel` matters: a touch that turns into a
       page scroll never fires `pointerup`, and without it the row would stay frozen for good. */
    this.addEventListener('pointerenter', this.hold);
    this.addEventListener('pointerleave', this.release);
    this.addEventListener('pointerdown', this.hold);
    this.addEventListener('pointerup', this.release);
    this.addEventListener('pointercancel', this.release);
    this.addEventListener('touchstart', this.hold, { passive: true });
    this.addEventListener('touchend', this.release, { passive: true });
    this.addEventListener('touchcancel', this.release, { passive: true });
    this.addEventListener('focusin', this.hold);
    this.addEventListener('focusout', this.release);

    this.onVisibility = () => (document.hidden ? this.hold() : this.release());
    document.addEventListener('visibilitychange', this.onVisibility);

    /* Off screen, it should not be costing anything. */
    if ('IntersectionObserver' in window) {
      this.seen = false;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.seen) {
              this.seen = true;
              this.release();
            } else if (!entry.isIntersecting && this.seen) {
              this.seen = false;
              this.hold();
            }
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(this);
      this.held = 1; // stays held until the observer says it is on screen
    }

    if (!this.reduced.matches && this.held === 0) this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.observer) this.observer.disconnect();
  }

  hold() {
    this.held += 1;
    this.stop();
  }

  release() {
    this.held = Math.max(0, this.held - 1);
    if (this.held === 0 && !this.reduced.matches) this.start();
  }

  start() {
    if (this.frame !== null) return;
    this.last = 0;
    this.frame = requestAnimationFrame(this.onFrame);
  }

  stop() {
    if (this.frame === null) return;
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  onFrame(now) {
    this.frame = requestAnimationFrame(this.onFrame);
    if (!this.last) {
      this.last = now;
      return;
    }

    const elapsed = Math.min(now - this.last, 64); /* a backgrounded tab must not jump on return */
    this.last = now;

    /* scrollLeft rounds to whole pixels in some engines, so sub-pixel movement is banked here
       rather than being lost every frame — otherwise a slow speed rounds to zero and nothing
       moves at all. */
    this.remainder += (this.speed * elapsed) / 1000;
    const step = Math.floor(this.remainder);
    if (step < 1) return;
    this.remainder -= step;

    const half = this.track.scrollWidth / 2;
    let next = this.scrollLeft + step;
    if (next >= half) next -= half;
    this.scrollLeft = next;
  }
}

if (!customElements.get('aa-hero-carousel')) {
  customElements.define('aa-hero-carousel', AAHeroCarousel);
}
